// Global
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { tv } from 'tailwind-variants';
import { useSitecore } from '@sitecore-content-sdk/nextjs';
import ImageWrapper, { normalizeImageUrl } from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { getTestProps } from 'lib/testing/utils';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useDictionary from 'lib/hooks/useDictionary';
import SvgIcon from 'helpers/SvgIcon/SvgIcon';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const getItemDisplayNames = (items, firstOnly = false)=>{
    const values = items?.map((item)=>item.fields?.displayName?.value?.trim()).filter((value)=>Boolean(value));
    if (!values?.length) {
        return undefined;
    }
    return firstOnly ? values[0] : values.join(', ');
};
const resolvePrimaryField = (primaryField, fallbackItems, firstFallbackOnly = false)=>{
    if (primaryField?.value?.trim()) {
        return primaryField;
    }
    const fallbackValue = getItemDisplayNames(fallbackItems, firstFallbackOnly);
    return fallbackValue ? {
        value: fallbackValue
    } : undefined;
};
const HeroContent = ({ title, description, cta1Link, cta2Link, cta3Link, heroTitle, heroDescription, params })=>{
    const styles = parseStyleParams(params, [
        'cta1',
        'cta2',
        'cta3'
    ]);
    const hasCtas = Boolean(cta1Link?.value?.href || cta2Link?.value?.href);
    const { contentContainerOld, headingText, descriptionTextOld, ctaWrapperOld, ctaContainer, cta, divider, headingContainerOld, linkedInCTA } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("div", {
        className: contentContainerOld(),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: headingContainerOld(),
                children: [
                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                        className: headingText(),
                        field: title,
                        fallbacks: [
                            heroTitle
                        ],
                        tag: "h1",
                        encode: false,
                        ...getTestProps(`expert-hero-title`)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: divider()
                    }),
                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                        className: descriptionTextOld(),
                        field: description,
                        fallbacks: [
                            heroDescription
                        ],
                        tag: "div",
                        ...getTestProps(`expert-hero-description`)
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: ctaWrapperOld(),
                children: [
                    hasCtas && /*#__PURE__*/ _jsxs("div", {
                        className: ctaContainer(),
                        children: [
                            /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-hero-button-color-1",
                                className: cta({
                                    style: styles.cta1?.ctaVariant
                                }),
                                ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                field: cta1Link,
                                remixIcon: "mail-line",
                                ctaIconAlignment: styles.cta1?.ctaIconAlignment,
                                suppressNewTabIcon: true,
                                ...getTestProps(`expert-hero-link-cta1`),
                                gtmEvent: createCTAClickGTMEvent({
                                    header: title?.value,
                                    ctaText: cta1Link?.value?.text,
                                    ctaType: 'blue',
                                    componentName: 'Core Header',
                                    ctaLink: cta1Link?.value?.href
                                })
                            }),
                            /*#__PURE__*/ _jsx(LinkWrapper, {
                                ctaComponentClass: "component-hero-button-color-2",
                                className: cta({
                                    style: styles.cta2?.ctaVariant
                                }),
                                ctaStyle: getCtaStyle(styles.cta2, 'primary'),
                                field: cta2Link,
                                remixIcon: "phone-line",
                                ctaIconAlignment: styles.cta2?.ctaIconAlignment,
                                suppressNewTabIcon: true,
                                ...getTestProps(`expert-hero-link-cta2`),
                                gtmEvent: createCTAClickGTMEvent({
                                    header: title?.value,
                                    ctaText: cta2Link?.value?.text,
                                    ctaType: 'blue',
                                    componentName: 'Core Header',
                                    ctaLink: cta2Link?.value?.href
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(LinkWrapper, {
                        ctaComponentClass: "default",
                        ctaVariant: "tertiaryInverse",
                        ...getTestProps(`expert-hero-link-cta3`),
                        className: linkedInCTA(),
                        field: cta3Link,
                        suppressNewTabIcon: true,
                        remixIcon: "linkedin-fill",
                        ctaIconAlignment: styles.cta3?.ctaIconAlignment
                    })
                ]
            })
        ]
    });
};
// Reusable content component for both variants
const HeroContentNew = ({ title, description, cta1Link, cta2Link, heroTitle, heroDescription, params, primaryLocation, primaryExpertise })=>{
    const styles = parseStyleParams(params, [
        'cta1',
        'cta2'
    ]);
    const hasCtas = Boolean(cta1Link?.value?.href || cta2Link?.value?.href);
    const { contentContainer, headingText, descriptionText, ctaWrapper, ctaContainer, cta, divider, headingContainer, locationText, expertiseText, detailinfo } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsxs("div", {
        className: contentContainer(),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: headingContainer(),
                children: [
                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                        className: headingText(),
                        field: title,
                        fallbacks: [
                            heroTitle
                        ],
                        tag: "h1",
                        encode: false,
                        ...getTestProps(`expert-hero-title`)
                    }),
                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                        className: descriptionText(),
                        field: description,
                        fallbacks: [
                            heroDescription
                        ],
                        tag: "div",
                        ...getTestProps(`expert-hero-description`)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: divider()
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: detailinfo(),
                        children: [
                            primaryExpertise?.value && /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: expertiseText(),
                                field: primaryExpertise,
                                tag: "p",
                                ...getTestProps(`expert-hero-expertise`)
                            }),
                            primaryLocation?.value && /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 justify-center md:justify-start",
                                children: [
                                    /*#__PURE__*/ _jsx(SvgIcon, {
                                        icon: "map-pin",
                                        size: "s",
                                        viewBox: "0 0 18 21"
                                    }),
                                    /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: locationText(),
                                        field: primaryLocation,
                                        tag: "p",
                                        ...getTestProps(`expert-hero-location`)
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: ctaWrapper(),
                children: hasCtas && /*#__PURE__*/ _jsxs("div", {
                    className: ctaContainer(),
                    children: [
                        /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "component-hero-button-color-1",
                            className: cta({
                                style: styles.cta1?.ctaVariant
                            }),
                            ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                            field: cta1Link,
                            remixIcon: "arrow-right-line",
                            ctaIconAlignment: styles.cta1?.ctaIconAlignment,
                            suppressNewTabIcon: true,
                            ...getTestProps(`expert-hero-link-cta1`),
                            gtmEvent: createCTAClickGTMEvent({
                                header: title?.value,
                                ctaText: cta1Link?.value?.text,
                                ctaType: 'blue',
                                componentName: 'Core Header',
                                ctaLink: cta1Link?.value?.href
                            })
                        }),
                        /*#__PURE__*/ _jsx(LinkWrapper, {
                            ctaComponentClass: "component-hero-button-color-2",
                            className: cta({
                                style: styles.cta2?.ctaVariant
                            }),
                            ctaStyle: getCtaStyle(styles.cta2, 'secondaryInverse'),
                            field: cta2Link,
                            remixIcon: "arrow-right-line",
                            ctaIconAlignment: styles.cta2?.ctaIconAlignment,
                            suppressNewTabIcon: true,
                            ...getTestProps(`expert-hero-link-cta2`),
                            gtmEvent: createCTAClickGTMEvent({
                                header: title?.value,
                                ctaText: cta2Link?.value?.text,
                                ctaType: 'blue',
                                componentName: 'Core Header',
                                ctaLink: cta2Link?.value?.href
                            })
                        })
                    ]
                })
            })
        ]
    });
};
const ExpertHero = (props)=>{
    const { page } = useSitecore();
    const currentPage = page?.layout?.sitecore?.route;
    const { firstname, lastname, jobtitle, email, telephoneNumber, picture, linkedInURL } = currentPage?.fields ?? {};
    const { getDictionaryValue } = useDictionary();
    const emailText = getDictionaryValue('Email');
    // Extract edit mode from the page context
    const isEditing = page?.mode?.isEditing;
    const cta1Link = {
        value: {
            href: `http://mailto:${email?.value}`,
            linktype: 'external',
            url: `mailto:${email?.value}`,
            target: '',
            text: `${emailText} ${firstname?.value}`,
            title: '',
            class: ''
        }
    };
    const cta2Link = {
        value: {
            href: `http://tel:${telephoneNumber?.value}`,
            linktype: 'external',
            url: `tel:${telephoneNumber?.value}`,
            target: '',
            text: telephoneNumber?.value,
            title: '',
            class: ''
        }
    };
    const cta3Link = {
        value: {
            href: linkedInURL?.value,
            linktype: 'external',
            url: linkedInURL?.value,
            text: ' ',
            title: `${firstname?.value} ${lastname?.value} LinkedIn`,
            class: ''
        }
    };
    // Build ImageObject JSON-LD if possible
    const newSrc = normalizeImageUrl(picture?.value?.src);
    const isAbsoluteUrl = typeof newSrc === 'string' && /^https?:\/\//i.test(newSrc);
    const imageSchema = isAbsoluteUrl && newSrc ? {
        '@context': 'http://schema.org/',
        '@type': 'ImageObject',
        description: picture?.value?.alt,
        image: newSrc
    } : undefined;
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full'; // Default: Full
    const heroImageLayout = props?.params?.heroImageLayout || 'Full';
    const { base, contentColumn, imageColumnBase, imageColumnRight, imageStyle } = TAILWIND_VARIANTS({
        heroLayout: layout,
        heroImageLayout: heroImageLayout
    });
    return (
        /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("section", {
                className: base(),
                "data-component": "authorable/shared/content/expert-hero",
                ...editingHydrationProps(isEditing),
                ...getTestProps(`component-expert-hero-${props?.rendering?.uid}`),
                "data-imageurl": picture?.value?.src,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: contentColumn(),
                        children: /*#__PURE__*/ _jsx(HeroContent, {
                            title: {
                                value: `${firstname?.value} ${lastname?.value}`
                            },
                            description: jobtitle,
                            cta1Link: cta1Link,
                            cta2Link: cta2Link,
                            cta3Link: cta3Link,
                            heroTitle: {
                                value: `${firstname?.value} ${lastname?.value}`
                            },
                            heroDescription: jobtitle,
                            params: props?.params
                        })
                    }),
                    picture?.value?.src && /*#__PURE__*/ _jsxs("div", {
                        className: `${imageColumnBase()} ${imageColumnRight()}`,
                        children: [
                            /*#__PURE__*/ _jsx(ImageWrapper, {
                                className: imageStyle(),
                                field: picture,
                                priority: true,
                                fetchPriority: "high",
                                sizes: "(max-width: 768px) 100vw, 50vw",
                                ...getTestProps(`expert-hero-image`)
                            }),
                            !isEditing && imageSchema && /*#__PURE__*/ _jsx("script", {
                                type: "application/ld+json",
                                dangerouslySetInnerHTML: {
                                    __html: JSON.stringify(imageSchema).replace(/</g, '\\u003c')
                                }
                            })
                        ]
                    })
                ]
            })
        })
    );
};
const ExpertNewVariant = (props)=>{
    const { page } = useSitecore();
    const currentPage = page?.layout?.sitecore?.route;
    const { firstname, lastname, jobtitle, email, telephoneNumber, picture, expertise, location, primaryExpertise, primaryLocation } = currentPage?.fields ?? {};
    const expertiseField = resolvePrimaryField(primaryExpertise, expertise, true);
    const locationField = resolvePrimaryField(primaryLocation, location, true);
    const isEditing = page?.mode?.isEditing;
    const { getDictionaryValue } = useDictionary();
    const emailText = getDictionaryValue('Email');
    const callText = getDictionaryValue('Call');
    const freeOnText = getDictionaryValue('FreeOn');
    const cta2Link = {
        value: {
            href: `http://mailto:${email?.value}`,
            linktype: 'external',
            url: `mailto:${email?.value}`,
            target: '',
            text: `${emailText} ${firstname?.value}`,
            title: '',
            class: ''
        }
    };
    const cta1Link = {
        value: {
            href: `http://tel:${telephoneNumber?.value}`,
            linktype: 'external',
            url: `tel:${telephoneNumber?.value}`,
            target: '',
            // text: telephoneNumber?.value,
            text: `${callText} ${firstname?.value} ${freeOnText} ${telephoneNumber?.value}`,
            title: '',
            class: ''
        }
    };
    // Build ImageObject JSON-LD if possible
    const newSrc = normalizeImageUrl(picture?.value?.src);
    const isAbsoluteUrl = typeof newSrc === 'string' && /^https?:\/\//i.test(newSrc);
    const imageSchema = isAbsoluteUrl && newSrc ? {
        '@context': 'http://schema.org/',
        '@type': 'ImageObject',
        description: picture?.value?.alt,
        image: newSrc
    } : undefined;
    const theme = props?.params?.selectTheme;
    const layout = props?.params?.layout || 'Full'; // Default: Full
    const heroImageLayout = props?.params?.heroImageLayout || 'Full';
    const { base, contentColumn, imageColumnBase, imageColumnRight, imageStyle } = TAILWIND_VARIANTS({
        heroLayout: layout,
        heroImageLayout: heroImageLayout
    });
    return (
        /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
            theme: theme,
            children: /*#__PURE__*/ _jsxs("section", {
                className: base(),
                "data-component": "authorable/shared/content/expert-hero",
                ...editingHydrationProps(isEditing),
                ...getTestProps(`component-expert-hero-${props?.rendering?.uid}`),
                "data-imageurl": picture?.value?.src,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: contentColumn(),
                        children: /*#__PURE__*/ _jsx(HeroContentNew, {
                            title: {
                                value: `${firstname?.value} ${lastname?.value}`
                            },
                            description: jobtitle,
                            cta1Link: cta1Link,
                            cta2Link: cta2Link,
                            heroTitle: {
                                value: `${firstname?.value} ${lastname?.value}`
                            },
                            heroDescription: jobtitle,
                            params: props?.params,
                            primaryLocation: locationField,
                            primaryExpertise: expertiseField
                        })
                    }),
                    picture?.value?.src && /*#__PURE__*/ _jsxs("div", {
                        className: `${imageColumnBase()} ${imageColumnRight()}`,
                        children: [
                            /*#__PURE__*/ _jsx(ImageWrapper, {
                                className: imageStyle(),
                                field: picture,
                                priority: true,
                                fetchPriority: "high",
                                sizes: "(max-width: 768px) 100vw, 50vw",
                                ...getTestProps(`expert-hero-image`)
                            }),
                            !isEditing && imageSchema && /*#__PURE__*/ _jsx("script", {
                                type: "application/ld+json",
                                dangerouslySetInnerHTML: {
                                    __html: JSON.stringify(imageSchema).replace(/</g, '\\u003c')
                                }
                            })
                        ]
                    })
                ]
            })
        })
    );
};
export const Default = ExpertHero;
export const ExpertNew = ExpertNewVariant;
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        layout: 'right',
        heroImageLayout: 'Full',
        heroVariant: 'Default'
    },
    slots: {
        base: [
            'flex',
            'md:gap-component-hero-space-between-desktop',
            'gap-component-hero-space-between-mobile',
            'items-center',
            'bg-component-hero-bg'
        ],
        cta: [],
        contentColumn: [
            'flex'
        ],
        imageColumnBase: [
            'w-full',
            'lg:w-1/2',
            'overflow-hidden',
            'aspect-[4/3]'
        ],
        imageColumnRight: [],
        imageColumnLeft: [],
        contentContainer: [
            'px-0',
            'w-full',
            'flex',
            'flex-col',
            'gap-spacing-spacing-40'
        ],
        ctaWrapper: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-40'
        ],
        ctaContainer: [
            'flex',
            'flex-wrap',
            'gap-spacing-spacing-8',
            'md:justify-normal'
        ],
        descriptionText: [
            'text-component-hero-body',
            'font-typography-font-family-body',
            'text-typography-font-size-xxsmall',
            'font-normal',
            'leading-[150%]',
            'text-center',
            'md:text-left',
            'max-w-full'
        ],
        locationText: [
            'text-component-hero-body',
            'font-typography-font-family-body',
            'text-typography-font-size-xxsmall',
            'font-normal',
            'leading-[150%]',
            'text-center',
            'md:text-left',
            'max-w-full',
            'text-component-hero-location'
        ],
        expertiseText: [
            'text-component-hero-body',
            'font-typography-font-family-body',
            'text-typography-font-size-xxsmall',
            'font-normal',
            'leading-[150%]',
            'text-center',
            'md:text-left',
            'max-w-full',
            'text-component-hero-location'
        ],
        headingText: [
            'font-normal',
            'text-component-hero-title',
            'font-typography-font-family-header',
            'text-typography-font-size-display',
            'leading-typography-line-height-display',
            'text-center',
            'md:text-left'
        ],
        imageStyle: [
            'w-full',
            'h-full',
            'overflow-hidden'
        ],
        divider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider',
            'mx-auto',
            'md:mx-0'
        ],
        headingContainer: [
            'flex',
            'flex-col',
            'gap-[20px]'
        ],
        detailinfo: [
            'flex',
            'flex-col',
            'gap-[10px]'
        ],
        linkedInCTA: [
            '!w-fit',
            '!gap-0',
            '!p-0',
            '[&>svg]:!text-white',
            'mx-auto',
            'md:mx-0'
        ],
        contentContainerOld: [
            'px-0',
            'w-full',
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        descriptionTextOld: [
            'text-component-hero-body',
            'font-typography-font-family-body',
            'text-typography-font-size-xsmall',
            'font-normal',
            'leading-[150%]',
            'text-center',
            'md:text-left',
            'max-w-full'
        ],
        ctaWrapperOld: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        headingContainerOld: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-12'
        ]
    },
    variants: {
        style: {
            tertiary: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    // TODO: NEED TO UPDAT FIGMA TOKENS.
                    // This is a temporary fix to make the cta link themeable.
                    'text-component-accordion-toggle-active'
                ]
            },
            tertiaryInverse: {
                cta: [
                    'flex',
                    'items-center',
                    'my-auto',
                    'gap-2',
                    'text-base',
                    'text-component-accordion-toggle-active'
                ]
            },
            primary: {
                cta: [
                    'px-8'
                ]
            },
            secondary: {
                cta: [
                    'px-8'
                ]
            },
            primaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            secondaryInverse: {
                cta: [
                    'px-8'
                ]
            },
            primaryOnCanvas: {
                cta: [
                    'px-8'
                ]
            },
            custom: {
                cta: []
            }
        },
        heroLayout: {
            Full: {
                base: [
                    'py-component-hero-padding-y',
                    'px-component-hero-padding-x',
                    'xl:px-spacing-spacing-80'
                ]
            },
            Wide: {
                base: [
                    'relative',
                    'before:z-[-1]',
                    'before:h-full',
                    'before:absolute',
                    'before:top-0',
                    'before:left-[calc(-50vw+50%)]',
                    'before:bg-component-hero-bg',
                    'before:right-[calc(-50vw+50%)]',
                    'py-component-hero-padding-y',
                    'xl:px-0'
                ]
            }
        },
        heroImageLayout: {
            Full: {
                imageColumnBase: [
                    'rounded-component-hero-image-border-radius',
                    'border',
                    'border-component-hero-image-border',
                    'border-component-hero-image-border-width'
                ],
                imageStyle: [
                    'object-cover',
                    'rounded-component-article-card-image-radius'
                ]
            },
            Contain: {
                imageColumnBase: [
                    'border-0',
                    'rounded-none'
                ],
                imageStyle: [
                    'object-contain',
                    'rounded-none'
                ]
            }
        },
        heroVariant: {
            Default: {
                base: [
                    'lg:flex-row',
                    'flex-col'
                ],
                contentColumn: [
                    'w-full',
                    'lg:w-1/2'
                ]
            }
        }
    }
});
